/**
 * Eingabe-Typen für den Stellungnahme-Generator.
 *
 * Bewusst lokal gehalten und NICHT im zentralen Schema persistiert — Berater-
 * Stammdaten und Auftraggeber-Daten leben aktuell außerhalb des Mandanten-
 * Datenmodells. Wenn Berater-Profile später in die DB wandern, werden hier
 * nur die Type-Imports getauscht; die Generator-API bleibt stabil.
 */

import type { TrafficLight, ScoreResult } from '../schema/score.js';
import type { Case } from '../schema/case.js';
import type { FinancialDataset } from '../schema/financialDataset.js';
import type { DimensionData, DimensionKey } from '../schema/dimensionData.js';

/** Petschwork-Stammdaten, die jeder Stellungnahme als Briefkopf vorausgehen. */
export interface ProviderProfile {
  /** Briefkopf-Name oben links (z.B. „Petschwork Consulting GmbH") */
  readonly name: string;
  readonly addressLine1: string;
  readonly addressLine2: string;
  readonly phoneEmail: string;
  readonly certifications: string;
  /** Berater, der die Stellungnahme zeichnet (Unterschrift unten) */
  readonly coachName: string;
}

/** Empfänger-Daten — bei B2G das Jobcenter, bei B2C der Mandant selbst. */
export interface RecipientInfo {
  readonly jobcenterName?: string;
  readonly jobcenterAddressLine1?: string;
  readonly jobcenterAddressLine2?: string;
  readonly jobcenterContact?: string;
  readonly jobcenterSalutation?: string;
  readonly jobcenterFileNumber?: string;
  readonly orderDate?: Date;
}

/** Mandanten-Stammdaten (Name, Geburtsdatum, Adresse). */
export interface ClientStaticData {
  readonly fullName: string;
  readonly firstName?: string;
  readonly birthDate?: Date;
  readonly address?: string;
}

/** Geschäftsdaten zum geprüften Unternehmen. */
export interface BusinessProfile {
  readonly branch: string;
  readonly legalForm: string;
  /** Gründungsdatum. Optional — fehlt es, wird „nicht erfasst" ausgewiesen,
   *  NIE ein Sentinel-Datum (früher `new Date(0)` → „01.01.1970"). */
  readonly startDate?: Date;
  readonly taxId: string;
  readonly description: string;
}

/**
 * Termine und Dokumenten-Liste, die in der Stellungnahme zitiert werden.
 * Datums-/Dauer-Felder sind optional: fehlen sie, weist das Gutachten
 * „nicht erfasst" aus statt einen Termin zu erfinden. `statementDate`
 * (Ausstellungsdatum) ist immer der Generierungszeitpunkt und bleibt Pflicht.
 */
export interface MeetingMetadata {
  readonly statementDate: Date;
  readonly dataSubmissionDate?: Date;
  readonly meetingDate?: Date;
  readonly meetingDurationMinutes?: number;
  readonly meetingFormat?: 'Videotermin' | 'Präsenztermin';
  /** Beginn des Leistungsbezugs (relevant für die 1-Jahres-Prüffrist § 10
   *  Abs. 2 Nr. 5 SGB II). Optional → „nicht erfasst". */
  readonly benefitStartDate?: Date;
  readonly documentListBwa: string;
  readonly documentListEks: string;
  readonly documentListAdditional?: string;
}

/**
 * Bewertungs-Texte, die der Berater im Workflow ergänzt (oder die aus
 * Heuristiken vorbefüllt werden). Pflicht bei B2G — bei B2C optional, weil
 * der Coach sie im Beratungsgespräch ausformuliert.
 */
export interface AssessmentTexts {
  readonly revenueAssessment?: string;
  readonly costsAssessment?: string;
  readonly profitAssessment?: string;
  readonly hourlyRateAssessment?: string;
  readonly liquidityAssessment?: string;
  readonly runwayAssessment?: string;
  readonly utilizationAssessment?: string;
  readonly gapInterpretation?: string;
  readonly verdictLongParagraph: string;
  readonly verdictReasoning: string;
  readonly recommendationDetailed: string;
  readonly conditionsParagraph?: string;
  readonly reevaluationProposal?: string;
  readonly constraintDiscussion: string;
  readonly riskIndicators: string;
  readonly strengthIndicators: string;
  /** Pro Dimension Kurz- und Langtext. Key = identity/activity/... */
  readonly dimensionTexts: Readonly<
    Record<DimensionKey, { readonly short: string; readonly detail: string }>
  >;
}

/**
 * Vollständige Eingabe für den Generator. Optionale Felder sind explizit
 * markiert; alles andere ist Pflicht.
 */
export interface BuildPlaceholdersInput {
  readonly caseData: Case;
  readonly client: ClientStaticData;
  readonly business: BusinessProfile;
  readonly score: ScoreResult;
  readonly financial: FinancialDataset;
  readonly dimensions: ReadonlyArray<DimensionData>;
  readonly provider: ProviderProfile;
  readonly recipient: RecipientInfo;
  readonly meeting: MeetingMetadata;
  readonly assessments: AssessmentTexts;
}

/** Output: flacher Record für docxtemplater. */
export type StatementPlaceholders = Readonly<Record<string, string>>;

/** Verdict-Mapping aus Ampel. */
export const VERDICT_BY_TRAFFIC: Readonly<Record<TrafficLight, string>> = {
  green: 'Tragfähigkeit gegeben',
  yellow: 'Tragfähigkeit eingeschränkt gegeben',
  red: 'Tragfähigkeit aktuell nicht gegeben',
};

export const RECOMMENDATION_SHORT_BY_TRAFFIC: Readonly<Record<TrafficLight, string>> = {
  green: 'Fortführung der Selbständigkeit empfohlen',
  yellow: 'Konditionierte Fortführung mit Auflagen',
  red: 'Vermittlungsbemühungen in abhängige Beschäftigung',
};

export const TRAFFIC_LABEL_DE: Readonly<Record<TrafficLight, string>> = {
  green: 'grün',
  yellow: 'gelb',
  red: 'rot',
};
